import React from "react";

const TYPE_ANONYMISATION = "ANONYMISATION_CAF";

/**
 * Affiche les compteurs d'un import.
 *
 * - variant="compact" : rendu condensé pour une ligne de tableau (liste des imports).
 * - variant="detail" (défaut) : grille de compteurs complète pour le bandeau de détail.
 *
 * Dans les deux cas, le nombre de lignes en erreur est mis en évidence dès
 * qu'il est supérieur à zéro, conformément au besoin métier (macro d'abord,
 * micro seulement quand ça a cassé).
 *
 * L'API renvoie les compteurs d'anonymisation pour tous les imports (à zéro
 * hors anonymisation) : c'est le type de l'import qui décide de les montrer,
 * ou un compteur non nul pour un import dont le type n'est pas transmis.
 */
const CompteursImport = ({ compteurs, type, variant = "detail" }) => {
  if (!compteurs) {
    return null;
  }

  const enErreur = compteurs.enErreur || 0;
  const anonymisation =
    type === TYPE_ANONYMISATION ||
    compteurs.anonymisees > 0 ||
    compteurs.introuvables > 0;

  const proceduresEnCours = compteurs.proceduresEnCours ?? 0;

  if (variant === "compact") {
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        {anonymisation ? (
          <div>
            {compteurs.lues} lue{compteurs.lues > 1 ? "s" : ""} ·{" "}
            {compteurs.anonymisees || 0} anonymisée
            {compteurs.anonymisees > 1 ? "s" : ""} ·{" "}
            {compteurs.introuvables || 0} introuvable
            {compteurs.introuvables > 1 ? "s" : ""} ·{" "}
            {proceduresEnCours} procédure{proceduresEnCours > 1 ? "s" : ""} en
            cours
          </div>
        ) : (
          <div>
            {compteurs.lues} lue{compteurs.lues > 1 ? "s" : ""} ·{" "}
            {compteurs.creees} créée{compteurs.creees > 1 ? "s" : ""} ·{" "}
            {compteurs.misesAJour} mise{compteurs.misesAJour > 1 ? "s" : ""} à
            jour
          </div>
        )}
        <div
          className={
            enErreur > 0 ? "text-danger font-weight-bold" : "text-muted"
          }
        >
          {enErreur} en erreur
        </div>
      </div>
    );
  }

  const tuiles = anonymisation
    ? [
        { label: "Lignes lues", value: compteurs.lues },
        { label: "Dossiers anonymisés", value: compteurs.anonymisees || 0 },
        { label: "Dossiers introuvables", value: compteurs.introuvables || 0 },
        {
          label: "Dossiers avec procédure en cours",
          value: proceduresEnCours,
        },
        // Même libellé que le compte rendu par mail : pour ce type, une ligne
        // ignorée est un dossier déjà anonymisé.
        { label: "Dossiers déjà anonymisés", value: compteurs.ignorees || 0 },
        { label: "Lignes en erreur", value: enErreur, danger: enErreur > 0 },
      ]
    : [
        { label: "Lignes lues", value: compteurs.lues },
        { label: "Allocataires créés", value: compteurs.creees },
        { label: "Allocataires mis à jour", value: compteurs.misesAJour },
        { label: "Lignes ignorées", value: compteurs.ignorees },
        { label: "Lignes en erreur", value: enErreur, danger: enErreur > 0 },
        { label: "Orientations en file", value: compteurs.orientationsEnFile },
      ];

  return (
    <div className="row">
      {tuiles.map((tuile) => (
        <div className="col-6 col-md-4 col-lg-2 mb-3" key={tuile.label}>
          <div
            className={
              "text-center p-3 rounded h-100 " +
              (tuile.danger ? "bg-danger text-white" : "bg-light")
            }
          >
            <div style={{ fontSize: "1.5rem" }} className="font-weight-bold">
              {tuile.value}
            </div>
            <div style={{ fontSize: "0.8rem" }}>{tuile.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompteursImport;
